import {
    AppBar,
    Container,
    makeStyles,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ValidatorForm } from 'react-material-ui-form-validator';
import DatePickerValidator from '../LeaveApplication/component/Validator/DatePickerValidator';
import Button from '@material-ui/core/Button';
import React from 'react';
import MUIDataTable from 'mui-datatables';
import { tableStyle, getMuiTheme, pxToRem } from '../UIComponent/CommonStyle';
import { getApprovalHistory } from './api/ApprovalAPI';
import LeaveRecordCollection from '../Function/LeaveRecordCollection';
import {
    setApprovalHistoryColumns,
    setApprovalHistoryOptions,
} from './TableSetting';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import moment from 'moment';
import GenerateCSV from './component/GenerateCSV';
import customAxios from '../../components/API/customAxios';
import TabPanel, { a11yProps } from '../UIComponent/TabPanel';
import { useParams, useLocation } from "react-router-dom";
import CompLeaveReqApprovalHistory from '../../components/CompLeaveReqsApproval/ApprovalHistory';

const useStyles = makeStyles((theme) => ({
    ...tableStyle(theme),
    container: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(4),
    },
    datePicker: {
        width: '25%',
        paddingRight: '20px',
    },
    datePickerInput: {
        textAlign: 'left',
        padding: '1rem 1rem',
        fontSize: pxToRem(14),
        border: '1px solid #B8B8B8',
        borderRadius: 5,
        outline: 'none',
        height: 'fit-content',
        minWidth: '100%',
        [breakpoints.up('md')]: {
            fontSize: pxToRem(16),
            height: pxToRem(61),
        },
    },
    datePickerCalendar: {
        border: '1px solid #B8B8B8',
        fontSize: 8,
        [breakpoints.up('md')]: {
            fontSize: 10,
        },
    },
    searchForm: {
        display: 'flex',
        padding: '0 0 10px',
        alignItems: 'center',
        marginLeft: 'auto',
        marginRight: 0,
    },
    searchButton: {
        height: 'fit-content',
        marginRight: '10px',
    },
    exportButton: {
        height: 'fit-content',
    },
    approvalHistoryHeader: {
        display: 'flex',
    },
    circularProgress: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(1),
    },
}));

const breakpoints = createBreakpoints({});

export default function ApprovalHistory({ isShiftDuty }) {
    const location = useLocation();
    const form = React.useRef();
    const classes = useStyles();
    const intl = useIntl();
    const today = moment();
    const lastMonth = moment().subtract(1, 'months');
    const [searchFromDate, setSearchFromDate] = React.useState({
        day: Number(lastMonth.format('DD')),
        month: Number(lastMonth.format('MM')),
        year: Number(lastMonth.format('YYYY')),
    });
    const [searchToDate, setSearchToDate] = React.useState({
        day: Number(today.format('DD')),
        month: Number(today.format('MM')),
        year: Number(today.format('YYYY')),
    });
    const [showTable, setShowTable] = React.useState(false);
    const [tableData, setTableData] = React.useState([]);
    const [showStaffNo, setShowStaffNo] = React.useState(false);
    const [tabValue, setTabValue] = React.useState(0);

    const tab1Label = intl.formatMessage({ id: 'navigation.leave' });
    const tab2Label = intl.formatMessage({ id: 'navigation.ot_compensation' });

    React.useEffect(() => {
        async function getPermission() {
            let isHrAdminRole = JSON.parse(localStorage.getItem('isHrAdminRole'));
            
            if (isHrAdminRole === null) {
                const permissionResponse = await customAxios.get('/get_permission');
                isHrAdminRole = permissionResponse.data.hr_admin === true;
                localStorage.setItem('isHrAdminRole', isHrAdminRole);
            }

            setShowStaffNo(isHrAdminRole);
        }
        getPermission();
    }, []);

    React.useEffect(() => {
        const tabIndex = new URLSearchParams(location.search).get('tabIndex') ?? 0;
        setTabValue(Number(tabIndex));
    }, [location]);

    React.useEffect(() => {
        handleSearch();
    }, []);

    const handleSearch = async () => {
        if (searchFromDate && searchToDate) {
            setShowTable(false);
            const getApprovesResponse = await getApprovalHistory(
                searchFromDate,
                searchToDate
            );
            if (getApprovesResponse.status === 200) {
                setTableData(
                    LeaveRecordCollection(
                        getApprovesResponse.data,
                        'LeaveApproval',
                        intl
                    )
                );
            } else {
                setTableData([]);
            }
            setShowTable(true);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const columns = setApprovalHistoryColumns(showStaffNo);
    const options = setApprovalHistoryOptions();

    const mainContent = (
        <Container maxWidth={false} className={classes.container}>
            <MuiThemeProvider theme={getMuiTheme()}>
                <div className={classes.approvalHistoryHeader}>
                    <Typography
                        component='h1'
                        variant='h5'
                        align='left'
                        style={{ fontWeight: 600 }}
                    >
                        <>
                            {intl.formatMessage({ id: 'navigation.approval_history' }) + ' - ' + intl.formatMessage({ id: 'navigation.leave' })}
                        </>
                    </Typography>
                    <ValidatorForm
                        className={classes.searchForm}
                        ref={form}
                        onSubmit={handleSearch}
                        onError={(errors) => errors}
                        style={{ position: 'relative', zIndex: '999' }}
                    >
                        <FormattedMessage id='common.from'>
                            {(message) => (
                                <DatePickerValidator
                                    wrapperClassName={classes.datePicker}
                                    value={searchFromDate}
                                    onChange={setSearchFromDate}
                                    inputPlaceholder={message + ' *'}
                                    shouldHighlightWeekends
                                    inputClassName={classes.datePickerInput}
                                    calendarClassName={
                                        classes.datePickerCalendar
                                    }
                                    calendarPopperPosition='bottom'
                                    validators={['required']}
                                />
                            )}
                        </FormattedMessage>
                        <FormattedMessage id='common.to'>
                            {(message) => (
                                <DatePickerValidator
                                    wrapperClassName={classes.datePicker}
                                    value={searchToDate}
                                    onChange={setSearchToDate}
                                    inputPlaceholder={message + ' *'}
                                    shouldHighlightWeekends
                                    inputClassName={classes.datePickerInput}
                                    calendarClassName={
                                        classes.datePickerCalendar
                                    }
                                    calendarPopperPosition='bottom'
                                    validators={['required']}
                                />
                            )}
                        </FormattedMessage>
                        <Button
                            onClick={handleSearch}
                            variant='contained'
                            className={classes.searchButton}
                        >
                            {intl.formatMessage({ id: 'table.search' })}
                        </Button>
                        {showTable ? (
                            <Button
                                variant='contained'
                                className={classes.exportButton}
                            >
                                <GenerateCSV
                                    csvData={tableData}
                                    fromDate={searchFromDate}
                                    toDate={searchToDate}
                                />
                            </Button>
                        ) : (
                            <div />
                        )}
                    </ValidatorForm>
                </div>
                {showTable ? (
                    <MUIDataTable
                        title={intl.formatMessage({
                            id: 'table.employee_list',
                        })}
                        data={tableData}
                        columns={columns}
                        options={options}
                    />
                ) : (
                    <div className={classes.circularProgress}>
                        <CircularProgress />
                    </div>
                )}
            </MuiThemeProvider>
        </Container>
    );

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <AppBar position='static' color='default'>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor='primary'
                    textColor='primary'
                    variant='scrollable'
                    scrollButtons='auto'
                >
                    <Tab label={tab1Label} {...a11yProps(0)} />
                    <Tab label={tab2Label} {...a11yProps(1)} />
                </Tabs>
            </AppBar>
            <TabPanel value={tabValue} index={0} disableGutters={true}>
                {mainContent}
            </TabPanel>
            <TabPanel value={tabValue} index={1} disableGutters={true}>
                <CompLeaveReqApprovalHistory showStaffNo={showStaffNo} />
            </TabPanel>
        </main>
    );
}
